import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown, MessageCircle } from "lucide-react";

const faqs = [
  {
    question: "B8ild est-il adapté à mon métier ?",
    answer:
      "B8ild est conçu pour tous les professionnels du BTP : électriciens, plombiers, maçons, peintres, couvreurs, entreprises générales... Quelle que soit votre spécialité, l'outil s'adapte à votre façon de travailler.",
  },
  {
    question: "Combien de temps pour prendre en main l'outil ?",
    answer:
      "10 minutes suffisent. Aucune formation nécessaire. L'interface est intuitive et pensée pour les artisans, pas pour les équipes IT. Créez votre premier chantier, importez une facture, et vous êtes opérationnel.",
  },
  {
    question: "Mes données sont-elles sécurisées ?",
    answer:
      "Oui. B8ild est hébergé en France sur des serveurs sécurisés et conforme RGPD. Vos données vous appartiennent. Elles sont chiffrées en transit et au repos. Vous pouvez les exporter ou les supprimer à tout moment.",
  },
  {
    question: "Puis-je annuler à tout moment ?",
    answer:
      "Oui, sans engagement. Vous pouvez annuler votre abonnement en un clic depuis les paramètres. Aucune pénalité, aucune question posée. Vos données restent accessibles pendant 30 jours après annulation.",
  },
  {
    question: "Puis-je gérer des équipes qui tournent sur plusieurs chantiers ?",
    answer:
      "Oui. B8ild gère les affectations par demi-journée. Les coûts de main d'œuvre sont calculés automatiquement pour chaque chantier, même avec des rotations fréquentes. Vous voyez en temps réel qui travaille où.",
  },
  {
    question: "Puis-je importer mes anciens chantiers ?",
    answer:
      "Oui, vous pouvez créer vos chantiers en cours et importer vos factures existantes. Le Scan IA extrait automatiquement les informations. Vous pouvez aussi renseigner manuellement les coûts déjà engagés.",
  },
  {
    question: "Quelle est la marge moyenne dans le BTP ?",
    answer:
      "La marge brute dans le BTP va de 25% à 50% selon le type de travaux. En rénovation courante : 30-50%. En dépannage ou urgence : 50-70%. En construction neuve et gros œuvre : 25-40%. Mais attention, la marge nette — ce qui reste après les frais généraux (loyer, assurances, véhicule, comptable) — tombe entre 3% et 8% en moyenne nationale selon la FFB. Prenez un plombier-chauffagiste qui fait 250 000€ de CA avec 35% de marge brute. Sa marge nette tourne autour de 6%, soit 15 000€ de bénéfice net annuel. Un seul chantier déficitaire de 5 000€ lui mange un tiers de son résultat. B8ild affiche la marge nette de chaque chantier en temps réel. Pas besoin d'attendre le bilan comptable pour savoir lequel vous coûte de l'argent.",
  },
  {
    question: "Comment calculer le coût de revient d'un chantier ?",
    answer:
      "La formule : déboursé sec (matériaux + main d'œuvre directe + sous-traitance) + frais de chantier (location matériel, benne, déplacements) + quote-part de frais généraux. Exemple concret : un chantier de rénovation à 45 000€ HT. Matériaux : 12 000€. Main d'œuvre : 3 ouvriers pendant 15 jours à 180€/jour chacun = 8 100€. Sous-traitance plaquiste : 3 500€. Frais de chantier : 1 200€. Frais généraux (15% du déboursé sec) : 3 720€. Coût de revient total : 28 520€. Marge prévisionnelle : 16 480€, soit 36,6%. Mais si les travaux durent 20 jours au lieu de 15, le coût main d'œuvre passe à 10 800€ et la marge fond à 31%. B8ild fait ce calcul en continu, à chaque facture importée.",
  },
  {
    question: "C'est quoi la différence entre marge brute et marge nette ?",
    answer:
      "La marge brute = chiffre d'affaires moins les coûts directs (matériaux, main d'œuvre, sous-traitance). La marge nette = marge brute moins les frais généraux (loyer atelier, assurance décennale, véhicule, comptable, téléphone). Exemple : un électricien fait un chantier à 8 000€ HT. Matériaux : 2 500€. Main d'œuvre : 2 200€. Marge brute : 3 300€, soit 41%. Il retire sa quote-part de frais généraux : 1 400€. Marge nette : 1 900€, soit 23,7%. C'est la marge nette qui compte. Un chantier à 40% de marge brute peut être à 5% de marge nette si vos frais généraux sont élevés. B8ild affiche les deux, chantier par chantier.",
  },
  {
    question: "Faut-il un logiciel pour un artisan seul ?",
    answer:
      "Oui, dès 2 chantiers en parallèle. Un artisan seul qui gère 3 chantiers simultanés avec Excel passe 4 à 6 heures par semaine sur le suivi administratif. C'est 200 à 300 heures par an. À 45€/h de taux horaire, ça représente 9 000 à 13 500€ de temps non facturé. Pire : sans suivi temps réel, vous découvrez les problèmes trop tard. Un maçon indépendant qui fait 120 000€ de CA et qui rate une dérive de 3 000€ sur un chantier perd 15% de son résultat net annuel. Avec B8ild, vous importez vos factures en 30 secondes. L'app calcule vos marges. Vous voyez d'un coup d'œil si un chantier dérape. Le gain : moins de paperasse, plus de marge, zéro mauvaise surprise.",
  },
];

function AccordionItem({
  index,
  faq,
  isOpen,
  onToggle,
}: {
  index: number;
  faq: { question: string; answer: string };
  isOpen: boolean;
  onToggle: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>(0);

  useEffect(() => {
    if (panelRef.current) {
      setMaxHeight(panelRef.current.scrollHeight);
    }
  }, [faq.answer]);

  return (
    <div
      className={`${index === 0 ? "border-t" : ""} border-b border-white/[0.06]`}
    >
      <button
        className={`w-full flex items-center gap-4 py-5 px-5 lg:px-6 text-left transition-all duration-250 border-l-2 ${
          isOpen
            ? "bg-[#141419] border-l-[#D4AF37]"
            : "border-l-transparent hover:bg-white/[0.02]"
        }`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span
          className={`text-xs font-bold tracking-wide flex-shrink-0 w-6 transition-colors duration-250 ${
            isOpen ? "text-[#D4AF37]" : "text-white/20"
          }`}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 text-base font-medium text-white leading-snug">
          {faq.question}
        </span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 transition-all duration-300 ${
            isOpen ? "rotate-180 text-[#D4AF37]" : "text-white/30"
          }`}
        />
      </button>

      <div
        ref={panelRef}
        className="overflow-hidden transition-all duration-400 ease-out"
        style={{
          maxHeight: isOpen ? `${maxHeight}px` : "0px",
          opacity: isOpen ? 1 : 0,
        }}
      >
        <p className="px-5 lg:px-6 pb-6 pl-[60px] lg:pl-[72px] text-[15px] text-white/50 leading-relaxed">
          {faq.answer}
        </p>
      </div>
    </div>
  );
}

const LandingFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = useCallback(
    (index: number) => {
      setOpenIndex((prev) => (prev === index ? -1 : index));
    },
    []
  );

  return (
    <section id="faq" className="relative py-24 lg:py-32 bg-[#08080C]">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#8B5CF6]/5 rounded-full blur-[200px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-14">
            <p className="text-sm uppercase tracking-wider text-white/40 font-medium mb-4 animate-fade-up">
              FAQ
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight animate-fade-up [animation-delay:100ms]">
              Questions
              <span className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent">
                {" "}fréquentes.
              </span>
            </h2>
          </div>

          {/* Accordéon amélioré */}
          <div className="animate-fade-up [animation-delay:200ms]">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                index={index}
                faq={faq}
                isOpen={openIndex === index}
                onToggle={() => handleToggle(index)}
              />
            ))}
          </div>

          {/* Contact card */}
          <div
            className="mt-12 rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.08] to-transparent animate-fade-up"
            style={{ animationDelay: "500ms" }}
          >
            <div className="rounded-2xl bg-[#0F0F14]/80 backdrop-blur-sm p-8 text-center">
              <div className="h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Vous avez d'autres questions ?
              </h3>
              <p className="text-white/50 mb-6">
                Notre équipe support répond en moins de 2 heures
              </p>
              <a
                href="mailto:support@b8ild.fr"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#D4AF37] text-black font-medium hover:opacity-90 transition-opacity"
              >
                Contactez-nous
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingFAQ;
